/**
 * DYNAMIC ASSUMPTIONS HELPER
 * 
 * Funções para gerar premissas de valuation dinamicamente
 * baseadas no último ano histórico da DRE
 * 
 * @version 1.0.0
 * @date 4 de novembro de 2025
 */

/**
 * Template de premissas de crescimento
 * Valores representam o padrão para cada empresa
 */
export interface GrowthTemplate {
  /** Nome da empresa */
  nome: 'Sétima' | '555 Studios';
  /** Crescimento ano 1 */
  ano1: number;
  /** Crescimento ano 2 */
  ano2: number;
  /** Crescimento ano 3 */
  ano3: number;
  /** Crescimento ano 4 */
  ano4: number;
  /** Crescimento ano 5 */
  ano5: number;
  /** Crescimento perpétuo */
  perpetuo: number;
}

/**
 * Template de margens EBITDA
 */
export interface MarginTemplate {
  /** Nome da empresa */
  nome: 'Sétima' | '555 Studios';
  /** Margem ano 1 */
  ano1: number;
  /** Margem ano 2 */
  ano2: number;
  /** Margem ano 3 */
  ano3: number;
  /** Margem ano 4 */
  ano4: number;
  /** Margem ano 5 */
  ano5: number;
}

/**
 * Template de CAPEX
 */
export interface CapexTemplate {
  /** Nome da empresa */
  nome: 'Sétima' | '555 Studios';
  /** CAPEX ano 1 */
  ano1: number;
  /** CAPEX ano 2 */
  ano2: number;
  /** CAPEX ano 3 */
  ano3: number;
  /** CAPEX ano 4 */
  ano4: number;
  /** CAPEX ano 5 */
  ano5: number;
}

/**
 * Template de NWC
 */
export interface NWCTemplate {
  /** Nome da empresa */
  nome: 'Sétima' | '555 Studios';
  /** NWC ano 1 */
  ano1: number;
  /** NWC ano 2 */
  ano2: number;
  /** NWC ano 3 */
  ano3: number;
  /** NWC ano 4 */
  ano4: number;
  /** NWC ano 5 */
  ano5: number;
}

/**
 * ✅ TEMPLATES PADRÃO - SÉTIMA (Cenário Base)
 */
export const SETIMA_GROWTH_TEMPLATE: GrowthTemplate = {
  nome: 'Sétima',
  ano1: 0.08,  // 8%
  ano2: 0.09,  // 9%
  ano3: 0.09,  // 9%
  ano4: 0.08,  // 8%
  ano5: 0.07,  // 7%
  perpetuo: 0.03,  // 3%
};

export const SETIMA_MARGIN_TEMPLATE: MarginTemplate = {
  nome: 'Sétima',
  ano1: 0.13,  // 13%
  ano2: 0.15,  // 15%
  ano3: 0.18,  // 18%
  ano4: 0.20,  // 20%
  ano5: 0.22,  // 22%
};

export const SETIMA_CAPEX_TEMPLATE: CapexTemplate = {
  nome: 'Sétima',
  ano1: 0.05,  // 5%
  ano2: 0.05,
  ano3: 0.05,
  ano4: 0.05,
  ano5: 0.05,
};

export const SETIMA_NWC_TEMPLATE: NWCTemplate = {
  nome: 'Sétima',
  ano1: 0.035,  // 3,5%
  ano2: 0.035,
  ano3: 0.035,
  ano4: 0.035,
  ano5: 0.035,
};

/**
 * ✅ TEMPLATES PADRÃO - 555 STUDIOS (Cenário Base)
 */
export const STUDIOS555_GROWTH_TEMPLATE: GrowthTemplate = {
  nome: '555 Studios',
  ano1: 0.20,  // 20%
  ano2: 0.18,  // 18%
  ano3: 0.15,  // 15%
  ano4: 0.12,  // 12%
  ano5: 0.10,  // 10%
  perpetuo: 0.03,  // 3%
};

export const STUDIOS555_MARGIN_TEMPLATE: MarginTemplate = {
  nome: '555 Studios',
  ano1: 0.35,  // 35%
  ano2: 0.36,  // 36%
  ano3: 0.37,  // 37%
  ano4: 0.38,  // 38%
  ano5: 0.38,  // 38%
};

export const STUDIOS555_CAPEX_TEMPLATE: CapexTemplate = {
  nome: '555 Studios',
  ano1: 0.20,  // 20%
  ano2: 0.18,  // 18%
  ano3: 0.16,  // 16%
  ano4: 0.15,  // 15%
  ano5: 0.15,  // 15%
};

export const STUDIOS555_NWC_TEMPLATE: NWCTemplate = {
  nome: '555 Studios',
  ano1: 0.06,  // 6%
  ano2: 0.06,
  ano3: 0.05,  // 5%
  ano4: 0.05,
  ano5: 0.05,
};

/**
 * ✅ FUNÇÃO PRINCIPAL: Gera premissas de crescimento dinâmicas
 * 
 * @param baseYear - Último ano histórico da DRE (ex: 2024, 2025, 2026)
 * @param template - Template de crescimento (valores para cada ano)
 * @returns Record<number, number> com 5 anos de projeção + perpetuo
 * 
 * @example
 * ```ts
 * // DRE até 2024 → projeta 2025-2029
 * const crescimento = generateDynamicGrowth(2024, SETIMA_GROWTH_TEMPLATE);
 * // → { 2025: 0.08, 2026: 0.09, 2027: 0.09, 2028: 0.08, 2029: 0.07, perpetuo: 0.03 }
 * 
 * // DRE até 2025 → projeta 2026-2030
 * const crescimento = generateDynamicGrowth(2025, SETIMA_GROWTH_TEMPLATE);
 * // → { 2026: 0.08, 2027: 0.09, 2028: 0.09, 2029: 0.08, 2030: 0.07, perpetuo: 0.03 }
 * ```
 */
export function generateDynamicGrowth(
  baseYear: number,
  template: GrowthTemplate
): Record<number, number> & { perpetuo: number } {
  const firstYear = baseYear + 1;
  
  return {
    [firstYear]: template.ano1,
    [firstYear + 1]: template.ano2,
    [firstYear + 2]: template.ano3,
    [firstYear + 3]: template.ano4,
    [firstYear + 4]: template.ano5,
    perpetuo: template.perpetuo,
  } as Record<number, number> & { perpetuo: number };
}

/**
 * ✅ Gera premissas de margem EBITDA dinâmicas
 * 
 * @param baseYear - Último ano histórico da DRE
 * @param template - Template de margens
 * @returns Record<number, number> com 5 anos de projeção
 */
export function generateDynamicMargins(
  baseYear: number,
  template: MarginTemplate
): Record<number, number> {
  const firstYear = baseYear + 1;
  
  return {
    [firstYear]: template.ano1,
    [firstYear + 1]: template.ano2,
    [firstYear + 2]: template.ano3,
    [firstYear + 3]: template.ano4,
    [firstYear + 4]: template.ano5,
  };
}

/**
 * ✅ Gera premissas de CAPEX dinâmicas
 * 
 * @param baseYear - Último ano histórico da DRE
 * @param template - Template de CAPEX
 * @returns Record<number, number> com 5 anos de projeção
 */
export function generateDynamicCapex(
  baseYear: number,
  template: CapexTemplate
): Record<number, number> {
  const firstYear = baseYear + 1;
  
  return {
    [firstYear]: template.ano1,
    [firstYear + 1]: template.ano2,
    [firstYear + 2]: template.ano3,
    [firstYear + 3]: template.ano4,
    [firstYear + 4]: template.ano5,
  };
}

/**
 * ✅ Gera premissas de NWC dinâmicas
 * 
 * @param baseYear - Último ano histórico da DRE
 * @param template - Template de NWC
 * @returns Record<number, number> com 5 anos de projeção
 */
export function generateDynamicNWC(
  baseYear: number,
  template: NWCTemplate
): Record<number, number> {
  const firstYear = baseYear + 1;
  
  return {
    [firstYear]: template.ano1,
    [firstYear + 1]: template.ano2,
    [firstYear + 2]: template.ano3,
    [firstYear + 3]: template.ano4,
    [firstYear + 4]: template.ano5,
  };
}

/**
 * ✅ Extrai valores de template de premissas existentes (anos fixos → template)
 * 
 * Útil para converter premissas antigas (2025-2029) em template reutilizável
 * 
 * @param premissas - Premissas com anos fixos (ex: { 2025: 0.08, 2026: 0.09, ... })
 * @returns Template com valores genéricos (ano1, ano2, ...)
 */
export function extractGrowthTemplate(
  premissas: Record<number, number> & { perpetuo: number }
): GrowthTemplate {
  const years = Object.keys(premissas)
    .filter(k => k !== 'perpetuo')
    .map(Number)
    .sort((a, b) => a - b);
  
  return {
    nome: 'Sétima', // Placeholder
    ano1: premissas[years[0]] || 0,
    ano2: premissas[years[1]] || 0,
    ano3: premissas[years[2]] || 0,
    ano4: premissas[years[3]] || 0,
    ano5: premissas[years[4]] || 0,
    perpetuo: premissas.perpetuo || 0.03,
  };
}

/**
 * ✅ Detecta último ano histórico da DRE automaticamente
 * 
 * @param dreData - Dados da DRE (setima e studio555)
 * @returns Último ano presente em qualquer das empresas
 * 
 * @example
 * ```ts
 * const baseYear = detectBaseYear(dreData);
 * // → 2024 (se DRE tem até 2024)
 * // → 2025 (se DRE tem até 2025)
 * ```
 */
export function detectBaseYear(dreData: {
  setima: { years: { year: number }[] };
  studio555: { years: { year: number }[] };
} | null): number {
  if (!dreData) return 2024; // Fallback padrão
  
  const setimaYears = dreData.setima.years.map(y => y.year);
  const studiosYears = dreData.studio555.years.map(y => y.year);
  const allYears = [...setimaYears, ...studiosYears];
  
  return allYears.length > 0 ? Math.max(...allYears) : 2024;
}
